/**
 * Shared signup pages (Foodbuy, HSSI) — sector-details lookup.
 * Trigger: the single [data-sector-lookup] control.
 * - "input" (Foodbuy Customer Number): optional digits-only filter
 *   (data-digits-only); debounced lookup, Enter (never submits) and change/blur.
 * - "select" (HSSI MARSHA Code): lookup on change; values in data-lookup-skip
 *   (e.g. "UNKNOWN") cancel any lookup and clear what a previous lookup filled.
 *   data-roster-source (JSON <script>) populates the options first.
 * Status text: data-msg-loading / -found / -not-found in data-lookup-status.
 * A match auto-fills Address Information plus the name fields on the trigger
 * (see lookupFillAddress), then resolves Country via the Google Geocoder.
 * Fields stay editable; lookup failures never block account creation.
 */

"use strict";

// ---- Constants ----

const DEFAULT_LOOKUP_BASE_URL = "https://api.restaurantware.com/api/v1";
const LOOKUP_DEFAULT_STORE_ID = "main_us";
const LOOKUP_DEBOUNCE_MS = 600;
const LOOKUP_STATUS_ERROR_CLASS = "is-error";
const LOOKUP_NON_DIGIT_PATTERN = /\D/g;
const LOOKUP_TRAILING_SLASH_PATTERN = /\/+$/;
const LOOKUP_MODE_INPUT = "input";
const LOOKUP_MODE_SELECT = "select";
const LOOKUP_ENTER_KEY = "Enter";
const LOOKUP_LOG_PREFIX = "[SignupLookup]";

const LOOKUP_TRIGGER_SELECTOR = "[data-sector-lookup]";
const LOOKUP_REGISTRATION_HASH_ID = "customer-registration-hash";
const LOOKUP_REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

const LOOKUP_FIELD_IDS = {
  STREET: "street_1",
  CITY: "customer_notes_City",
  STATE: "customer_notes_Province",
  STATE_CODE: "customer_notes_ProvinceCode",
  ZIP: "customer_notes_Zip",
  COUNTRY_SELECT: "customer_country",
  COUNTRY_HIDDEN: "customer_notes_country",
  COUNTRY_CODE: "customer_country_code",
};

const GEOCODE_COMPONENT_COUNTRY = "country";
const GEOCODE_COMPONENT_STATE = "administrative_area_level_1";
const GEOCODER_STATUS_OK = "OK";

// ---- Roster (select options) population ----

/**
 * Read codes from a JSON <script>: an array, or an object of arrays (flattened).
 * Parse failures or other shapes yield an empty list.
 * @param {string} sourceId - id of the JSON <script> element.
 * @returns {string[]} Trimmed, unique, numeric-aware sorted codes.
 */
function lookupReadRosterCodes(sourceId) {
  const dataElement = document.getElementById(sourceId);
  if (null === dataElement) return [];

  let raw;
  try {
    raw = JSON.parse(dataElement.textContent);
  } catch (err) {
    console.warn(`${LOOKUP_LOG_PREFIX} Could not parse #${sourceId} JSON.`, err);
    return [];
  }

  let values = [];
  if (Array.isArray(raw)) {
    values = raw;
  } else if (raw !== null && typeof raw === "object") {
    values = Object.values(raw).reduce((flattened, group) => {
      return Array.isArray(group) ? flattened.concat(group) : flattened;
    }, []);
  }

  const codes = new Set();
  values
    .map((value) => String(value).trim())
    .filter((value) => value !== "")
    .forEach((value) => codes.add(value));

  return Array.from(codes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/**
 * Append each code as an <option> (createElement/textContent only, no innerHTML).
 * @param {HTMLSelectElement} select
 * @param {string[]} codes
 */
function lookupPopulateOptions(select, codes) {
  codes.forEach((code) => {
    const option = document.createElement("option");
    option.value = code;
    option.textContent = code;
    select.appendChild(option);
  });
}

// ---- Address auto-fill ----

/**
 * Set a field's value only for a non-empty string and clear its custom
 * validity. Missing/empty values leave the field untouched.
 * @param {HTMLInputElement|HTMLSelectElement|null} field
 * @param {*} value
 */
function lookupSetElementValue(field, value) {
  if (typeof value !== "string" || value.trim() === "") return;
  if (null === field) return;

  field.value = value;
  field.setCustomValidity("");
}

/**
 * Select the option whose text (fallback: value) matches name. Never dispatches
 * change (the country select has onchange="updateStates()").
 * @param {HTMLSelectElement} select
 * @param {string} name
 * @returns {boolean} true when a matching option was found and applied.
 */
function lookupSelectCountryOption(select, name) {
  const options = Array.from(select.options);
  const option = options.find((opt) => opt.text === name) ?? options.find((opt) => opt.value === name);

  if (!option) return false;

  select.value = option.value;
  select.setCustomValidity("");
  return true;
}

/**
 * Remove a trailing " - {code}" (hyphen/en/em dash, any spacing) from dcnName:
 * "Some Location - 595454" -> "Some Location".
 * @param {*} dcnName
 * @param {string} code - the looked-up DCN number / MARSHA code.
 * @returns {*} cleaned string, or the input unchanged when it isn't a string.
 */
function lookupCleanDcnName(dcnName, code) {
  if (typeof dcnName !== "string" || !code) return dcnName;
  const escapedCode = code.replace(LOOKUP_REGEX_SPECIAL_CHARS, "\\$&");
  return dcnName.replace(new RegExp(`\\s*[-–—]\\s*${escapedCode}\\s*$`, "i"), "").trim();
}

/**
 * Fill Address Information from a match, plus the name fields named by the
 * trigger's data-fill-location-name / data-fill-dcn-name selectors
 * (distributionCenterName or legacy dcnName, " - {code}" stripped); then
 * resolve Country via the Geocoder.
 * @param {Object} match
 * @param {string} code
 * @param {HTMLElement} trigger
 */
function lookupFillAddress(match, code, trigger) {
  const byId = (id) => document.getElementById(id);
  const street1 = [match?.street1, match?.street2].filter(Boolean).join(", ");
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.STREET), street1);
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.CITY), match?.city);
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.STATE), match?.state || match?.stateCode);
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.STATE_CODE), match?.stateCode);
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.ZIP), match?.postalCode);

  if (typeof match?.country === "string" && match.country.trim() !== "") {
    const countrySelect = byId(LOOKUP_FIELD_IDS.COUNTRY_SELECT);
    if (null !== countrySelect) lookupSelectCountryOption(countrySelect, match.country);
    lookupSetElementValue(byId(LOOKUP_FIELD_IDS.COUNTRY_HIDDEN), match.country);
  }
  lookupSetElementValue(byId(LOOKUP_FIELD_IDS.COUNTRY_CODE), match?.countryCode);

  const bySelector = (selector) => (selector ? document.querySelector(selector) : null);
  const cleanedDcnName = lookupCleanDcnName(match?.distributionCenterName ?? match?.dcnName, code);
  lookupSetElementValue(bySelector(trigger.dataset.fillLocationName), cleanedDcnName);
  lookupSetElementValue(bySelector(trigger.dataset.fillDcnName), cleanedDcnName);

  lookupFillCountryFromGeocoder();
}

let lookupGeocodeRequestCounter = 0;

/**
 * Undo lookupFillAddress: blank street/city/state/state code/zip and the name
 * fields named on the trigger (country is left alone), drop any pending
 * Geocoder response.
 *
 * @param {HTMLElement} trigger
 */
function lookupClearAddress(trigger) {
  lookupGeocodeRequestCounter++;
  const fields = [
    LOOKUP_FIELD_IDS.STREET,
    LOOKUP_FIELD_IDS.CITY,
    LOOKUP_FIELD_IDS.STATE,
    LOOKUP_FIELD_IDS.STATE_CODE,
    LOOKUP_FIELD_IDS.ZIP,
  ].map((id) => document.getElementById(id));
  [trigger.dataset.fillLocationName, trigger.dataset.fillDcnName].forEach((selector) => {
    if (selector) fields.push(document.querySelector(selector));
  });
  fields.forEach((field) => {
    if (null === field) return;
    field.value = "";
    field.setCustomValidity("");
  });
}

// ---- Country via Google Geocoder ----

/**
 * Read a field's trimmed value by id ("" when missing).
 * @param {string} id
 * @returns {string}
 */
function lookupGetFieldValue(id) {
  const field = document.getElementById(id);
  return field ? field.value.trim() : "";
}

/**
 * Apply the Geocoder country (and a missing state code) to Address Information.
 * @param {Array<{types: string[], long_name: string, short_name: string}>} addressComponents
 */
function lookupApplyGeocodedComponents(addressComponents) {
  const countryComponent = addressComponents.find((component) =>
    component.types.includes(GEOCODE_COMPONENT_COUNTRY)
  );

  if (countryComponent) {
    const countrySelect = document.getElementById(LOOKUP_FIELD_IDS.COUNTRY_SELECT);
    if (null !== countrySelect) lookupSelectCountryOption(countrySelect, countryComponent.long_name);

    const countryHidden = document.getElementById(LOOKUP_FIELD_IDS.COUNTRY_HIDDEN);
    if (null !== countryHidden) countryHidden.value = countryComponent.long_name;

    const countryCode = document.getElementById(LOOKUP_FIELD_IDS.COUNTRY_CODE);
    if (null !== countryCode) countryCode.value = countryComponent.short_name;
  }

  const stateCodeField = document.getElementById(LOOKUP_FIELD_IDS.STATE_CODE);
  if (null !== stateCodeField && stateCodeField.value.trim() === "") {
    const stateComponent = addressComponents.find((component) =>
      component.types.includes(GEOCODE_COMPONENT_STATE)
    );
    if (stateComponent) stateCodeField.value = stateComponent.short_name;
  }
}

/**
 * Resolve Country for the current address via the Google Maps Geocoder. Never
 * throws; failures leave it for manual entry. Stale responses are discarded.
 */
function lookupFillCountryFromGeocoder() {
  const query = [
    lookupGetFieldValue(LOOKUP_FIELD_IDS.STREET),
    lookupGetFieldValue(LOOKUP_FIELD_IDS.CITY),
    lookupGetFieldValue(LOOKUP_FIELD_IDS.STATE),
    lookupGetFieldValue(LOOKUP_FIELD_IDS.ZIP),
  ].filter(Boolean).join(", ");
  if (!query) return;

  if (!window.google?.maps?.Geocoder) {
    console.warn(`${LOOKUP_LOG_PREFIX} Google Maps not loaded; country left for manual entry.`);
    return;
  }

  const requestId = ++lookupGeocodeRequestCounter;

  new google.maps.Geocoder().geocode({ address: query }, (results, status) => {
    if (requestId !== lookupGeocodeRequestCounter) return; // stale response

    if (status !== GEOCODER_STATUS_OK || !results || results.length === 0) {
      console.warn(`${LOOKUP_LOG_PREFIX} Geocoder status:`, status);
      return;
    }

    const addressComponents = results[0].address_components;
    if (!Array.isArray(addressComponents)) return;

    lookupApplyGeocodedComponents(addressComponents);
  });
}

// ---- Sector-details request ----

/**
 * Build the sector-details URL for a code from the data attributes rendered on
 * #customer-registration-hash (page id, hash key, sector type, base URL).
 * @param {string} code - Foodbuy Customer Number or MARSHA code.
 * @returns {string|null} null when the page-id/hash/sector-type data is unavailable.
 */
function lookupBuildUrl(code) {
  const hashElement = document.getElementById(LOOKUP_REGISTRATION_HASH_ID);
  const pageId = hashElement?.dataset.pageId;
  const hashKey = hashElement?.dataset.hashKey;
  const sectorType = hashElement?.dataset.sectorType?.trim();

  if (!pageId || !hashKey || !sectorType) return null;

  const configuredBase = (hashElement.dataset.lookupBaseUrl ?? "").trim().replace(LOOKUP_TRAILING_SLASH_PATTERN, "");
  const baseUrl = configuredBase || DEFAULT_LOOKUP_BASE_URL;
  const storeId = window.STORE_ID ?? LOOKUP_DEFAULT_STORE_ID;

  return (
    `${baseUrl}/${storeId}/storefront/sector-details/${encodeURIComponent(pageId)}` +
    `?key=${encodeURIComponent(hashKey)}&dcnNumber=${encodeURIComponent(code)}` +
    `&sector_type=${encodeURIComponent(sectorType)}`
  );
}

/**
 * Create the lookup controller for one trigger element.
 *
 * @param {HTMLInputElement|HTMLSelectElement} trigger
 * @returns {{run: function(string, function(): void=): void, cancel: function(): void, clear: function(): void}}
 */
function lookupCreateController(trigger) {
  const statusElement = document.getElementById(trigger.dataset.lookupStatus ?? "");
  const messages = {
    loading: trigger.dataset.msgLoading ?? "",
    found: trigger.dataset.msgFound ?? "",
    notFound: trigger.dataset.msgNotFound ?? "",
  };
  let requestCounter = 0;
  let autoFilled = false; // true while the address fields hold looked-up values

  /**
   * Show/update the lookup status line.
   *
   * @param {string} message - Empty string hides the status line.
   * @param {boolean} isError
   */
  function setStatus(message, isError) {
    if (null === statusElement) return;
    statusElement.textContent = message;
    statusElement.hidden = !message;
    statusElement.classList.toggle(LOOKUP_STATUS_ERROR_CLASS, Boolean(isError));
  }

  /** Discard any in-flight lookup and hide the status line. */
  function cancel() {
    requestCounter++;
    setStatus("", false);
  }

  /** Clears looked-up values (only those a lookup filled). */
  function clearAutoFilled() {
    if (!autoFilled) return;
    lookupClearAddress(trigger);
    autoFilled = false;
  }

  /** Cancel, then clear what a previous lookup filled (manual entries stay). */
  function clear() {
    cancel();
    clearAutoFilled();
  }

  /**
   * Run the lookup for a code and apply the result.
   *
   * @param {string} code
   * @param {function(): void} [onRequestFailed] - called when the request itself fails.
   */
  function run(code, onRequestFailed) {
    const url = lookupBuildUrl(code);
    if (null === url) {
      console.warn(`${LOOKUP_LOG_PREFIX} Missing page id, hash key or sector type; skipping lookup.`);
      setStatus(messages.notFound, true);
      return;
    }

    const requestId = ++requestCounter;
    setStatus(messages.loading, false);
    console.debug(`${LOOKUP_LOG_PREFIX} Requesting`, url);

    fetch(url, { method: "GET" })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (requestId !== requestCounter) return;
        const match = Array.isArray(json?.data) ? json.data[0] : null;
        if (!match) {
          clearAutoFilled();
          setStatus(messages.notFound, true);
          return;
        }
        lookupFillAddress(match, code, trigger);
        autoFilled = true;
        setStatus(messages.found, false);
      })
      .catch((err) => {
        if (requestId !== requestCounter) return;
        onRequestFailed?.();
        console.warn(`${LOOKUP_LOG_PREFIX} Sector-details lookup failed.`, err);
        setStatus(messages.notFound, true);
      });
  }

  return { run, cancel, clear };
}

// ---- Trigger wiring ----

/**
 * Wire a text-input trigger (Foodbuy Customer Number).
 * @param {HTMLInputElement} input
 * @param {{run: function(string, function(): void): void, cancel: function(): void}} controller
 */
function lookupWireInput(input, controller) {
  const digitsOnly = input.hasAttribute("data-digits-only");
  let lastLookupValue = "";
  let debounceTimer = null;

  /** Look up the field's current value (skipping a repeat of the last value). */
  function runForCurrentValue() {
    const code = input.value.trim();
    if (!code) {
      lastLookupValue = "";
      controller.cancel();
      return;
    }
    if (code === lastLookupValue) return; // avoid duplicate calls for the same value

    lastLookupValue = code;
    controller.run(code, () => {
      lastLookupValue = ""; // allow retrying the same value after a failure
    });
  }

  input.addEventListener("input", () => {
    if (digitsOnly) {
      const filtered = input.value.replace(LOOKUP_NON_DIGIT_PATTERN, "");
      if (filtered !== input.value) input.value = filtered;
    }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runForCurrentValue, LOOKUP_DEBOUNCE_MS);
  });

  input.addEventListener("change", () => {
    clearTimeout(debounceTimer);
    runForCurrentValue();
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === LOOKUP_ENTER_KEY) {
      event.preventDefault();
      clearTimeout(debounceTimer);
      runForCurrentValue();
    }
  });
}

/**
 * Wire a select trigger (HSSI MARSHA Code): optional roster population, then a
 * lookup on each change; empty / data-lookup-skip values clear instead.
 *
 * @param {HTMLSelectElement} select
 * @param {{run: function(string): void, clear: function(): void}} controller
 */
function lookupWireSelect(select, controller) {
  const rosterSource = select.dataset.rosterSource;
  if (rosterSource) lookupPopulateOptions(select, lookupReadRosterCodes(rosterSource));

  const skipValues = (select.dataset.lookupSkip ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value !== "");

  select.addEventListener("change", () => {
    const value = select.value;
    if (value === "" || skipValues.includes(value)) {
      controller.clear();
      return;
    }
    controller.run(value);
  });
}

// ---- Initialisation ----

document.addEventListener("DOMContentLoaded", () => {
  const trigger = document.querySelector(LOOKUP_TRIGGER_SELECTOR);
  if (null === trigger) return;

  const controller = lookupCreateController(trigger);
  const mode = trigger.dataset.sectorLookup;

  if (mode === LOOKUP_MODE_INPUT) {
    lookupWireInput(trigger, controller);
  } else if (mode === LOOKUP_MODE_SELECT) {
    lookupWireSelect(trigger, controller);
  }
});
